/**
 * The two typefaces, served from our own origin.
 *
 * Bricolage Grotesque sets headings and Albert Sans sets everything else. Both are under the SIL Open
 * Font License, so the files are vendored in `frontend/components/public/fonts` and shipped inside the
 * image rather than fetched at runtime.
 *
 * ## Why not link to Google
 *
 * These *are* Google fonts, and linking would download the same bytes, so the choice is only about who
 * serves them.
 *
 * - **It is slower.** Cross-origin font caching ended when browsers partitioned the HTTP cache, so
 *   there is no longer a shared copy to hit. A link costs DNS, TLS and a stylesheet fetch against
 *   `fonts.googleapis.com`, which then points at `fonts.gstatic.com` - a third origin, a third
 *   connection - before a glyph arrives. Same-origin, the file rides a warm connection and is preloaded
 *   from each surface's `index.html`.
 * - **DDDS is deployed inside the customer's own network** (D-01). A link makes every page view depend
 *   on that network reaching Google, and the failure mode is a silent fallback on an instance we cannot
 *   see into.
 * - **It would put a client's IP address in a third party's logs** on every view of a financial firm's
 *   document portal, which is a question for a D-14 reviewer rather than an engineering one.
 * - The nginx templates set a Content-Security-Policy carrying `frame-ancestors` and nothing else
 *   today. Whoever tightens it should not have to discover a font origin while doing so.
 *
 * ## Only the weights that are used
 *
 * Five files, about 74 kB in total. Bricolage at 600 and 700, Albert Sans at 400, 500 and 600 - which
 * is what `surface.ts` actually asks for. Adding a weight here that no rule uses costs a prospect on
 * cellular real time for nothing, and a weight that is *missing* is worse than absent: the browser
 * synthesises a bold by smearing the regular, which looks like a rendering defect.
 *
 * `font-display: swap` deliberately. The alternative is invisible text while a face downloads, and the
 * dominant reader here is somebody on a phone who has just tapped a link in an email.
 *
 * A string rather than a `.css` file for the reason `styles.ts` gives: this package is consumed as
 * source with no build step, so it must make no bundler assumptions. Each surface injects it, and each
 * surface's Vite `publicDir` points at `frontend/components/public` so the URLs below resolve.
 */
export const fontFaces = `
  @font-face {
    font-family: 'Bricolage Grotesque';
    font-style: normal;
    font-weight: 600;
    font-display: swap;
    src: url('/fonts/bricolage-grotesque-latin-600-normal.woff2') format('woff2');
  }
  @font-face {
    font-family: 'Bricolage Grotesque';
    font-style: normal;
    font-weight: 700;
    font-display: swap;
    src: url('/fonts/bricolage-grotesque-latin-700-normal.woff2') format('woff2');
  }
  @font-face {
    font-family: 'Albert Sans';
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: url('/fonts/albert-sans-latin-400-normal.woff2') format('woff2');
  }
  @font-face {
    font-family: 'Albert Sans';
    font-style: normal;
    font-weight: 500;
    font-display: swap;
    src: url('/fonts/albert-sans-latin-500-normal.woff2') format('woff2');
  }
  @font-face {
    font-family: 'Albert Sans';
    font-style: normal;
    font-weight: 600;
    font-display: swap;
    src: url('/fonts/albert-sans-latin-600-normal.woff2') format('woff2');
  }
`;
